// src/utils/phoneUtils.ts

/**
 * Formatea un número de teléfono añadiendo un espacio opcional después del código de país.
 * @param countryCode - El código de país (ej: '+52') o null.
 * @param phoneNumber - El número de teléfono.
 * @returns El número formateado como string (ej: "+52 6861234567") o string vacío.
 */
export const formatPhoneNumber = (countryCode: string | null | undefined, phoneNumber: string | null | undefined): string => {
    // Devuelve string vacío si no hay número para evitar mostrar solo el código o espacios.
    if (!phoneNumber) {
        return '';
    }
    return `${countryCode || ''} ${phoneNumber}`.trim();
};

/**
 * Genera un enlace 'tel:' para iniciar llamadas.
 * @param countryCode - El código de país (ej: '+52') o null.
 * @param phoneNumber - El número de teléfono.
 * @returns El enlace 'tel:' formateado o '#' si no hay número.
 */
export const getPhoneLink = (countryCode: string | null | undefined, phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) {
        return '#';
    }
    // Limpia el número combinado (código + número) quitando todo lo que no sea dígito.
    const cleanNumber = `${countryCode || ''}${phoneNumber || ''}`.replace(/\D/g, '');
    // Devuelve '#' si el número limpio está vacío para evitar 'tel:' vacío.
    return cleanNumber ? `tel:${cleanNumber}` : '#';
};

/**
 * Genera un enlace para iniciar chat de WhatsApp, con diferente formato según la plataforma.
 * @param countryCode - El código de país (ej: '+52') o null.
 * @param phoneNumber - El número de teléfono.
 * @param message - Mensaje opcional para precompletar en WhatsApp.
 * @returns El enlace de WhatsApp formateado para web o móvil, o '#' si no hay número.
 */
export const getWhatsAppLink = (
    countryCode: string | null | undefined, 
    phoneNumber: string | null | undefined, 
    message: string = ''
): string => {
    if (!phoneNumber) {
        return '#';
    }
    
    // Limpia el número combinado, quita no-dígitos y el '+' inicial.
    const cleanNumber = `${countryCode || ''}${phoneNumber || ''}`.replace(/\D/g, '').replace(/^\+/, '');

    // Devuelve '#' si el número limpio está vacío.
    if (!cleanNumber) {
        return '#';
    }

    // MX: WhatsApp exige 521 + 10 dígitos. Mismo criterio que wahaService.normalizePhoneNumber.
    let waNumber = cleanNumber;
    if (waNumber.length === 12 && waNumber.startsWith('52') && !waNumber.startsWith('521')) {
        waNumber = '521' + waNumber.substring(2);   // 52+10 → 521+10
    } else if (waNumber.length === 10 && /^[2-9]/.test(waNumber)) {
        waNumber = '521' + waNumber;                 // 10 sueltos → 521
    }

    // Codificar el mensaje para la URL
    const encodedMessage = encodeURIComponent(message);

    // Detectar si es dispositivo móvil
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        typeof navigator !== 'undefined' ? navigator.userAgent : ''
    );

    // Usar formato diferente según la plataforma
    if (isMobile) {
        return `whatsapp://send?phone=${waNumber}&text=${encodedMessage}`;
    } else {
        return `https://web.whatsapp.com/send?phone=${waNumber}&text=${encodedMessage}`;
    }
};